import type { ICustomElement, IMessageElement } from '@chainlit/react-client';

const DASHBOARD_ELEMENT_NAME = 'DashboardPreview';
const ARTIFACT_ELEMENT_NAMES = ['ArtifactPreview', DASHBOARD_ELEMENT_NAME];

export const isArtifactElement = (
    element: IMessageElement
): element is ICustomElement =>
    element.type === 'custom' &&
    ARTIFACT_ELEMENT_NAMES.includes((element as ICustomElement).name);

export const isDashboardElement = (
    element: IMessageElement
): element is ICustomElement =>
    element.type === 'custom' &&
    (element as ICustomElement).name === DASHBOARD_ELEMENT_NAME;

export const dashboardIdOf = (element: IMessageElement): string | undefined =>
    isDashboardElement(element)
        ? (element.props?.dashboardId as string | undefined)
        : undefined;

export const artifactFilename = (element: IMessageElement): string => {
    const props = (element as ICustomElement).props;
    if (isDashboardElement(element)) {
        return (props?.title as string | undefined) || 'Dashboard';
    }
    return (props?.filename as string | undefined) || element.name;
};

const artifactIdentity = (element: IMessageElement): string => {
    const dashboardId = dashboardIdOf(element);
    return dashboardId ? `dashboard:${dashboardId}` : artifactFilename(element);
};

const artifactKey = (forId: string | undefined, identity: string) =>
    `${forId ?? ''}:${identity}`;

export const artifactKeysOf = (elements: IMessageElement[]): Set<string> =>
    new Set(
        elements
            .filter(isArtifactElement)
            .map((el) => artifactKey(el.forId, artifactIdentity(el)))
    );

export const isArtifactFile = (
    element: IMessageElement,
    artifactKeys: Set<string>
): boolean =>
    element.type === 'file' &&
    artifactKeys.has(artifactKey(element.forId, element.name));

export const findArtifactFor = (
    elements: IMessageElement[],
    forId: string | undefined,
    filename: string | undefined
): IMessageElement | undefined =>
    filename
        ? elements.find(
              (el) =>
                  isArtifactElement(el) &&
                  el.forId === forId &&
                  artifactFilename(el) === filename
          )
        : undefined;

export const latestArtifactVersions = (
    artifacts: IMessageElement[]
): IMessageElement[] => {
    const latestByIdentity = new Map<string, IMessageElement>();
    for (const artifact of artifacts) {
        const identity = artifactIdentity(artifact);
        latestByIdentity.delete(identity);
        latestByIdentity.set(identity, artifact);
    }
    return [...latestByIdentity.values()];
};

export const resolveLatestArtifactId = (
    allArtifacts: IMessageElement[],
    latestArtifacts: IMessageElement[],
    elementId: string | null
): string | null => {
    const requested = allArtifacts.find((el) => el.id === elementId);
    if (!requested) return null;
    const identity = artifactIdentity(requested);
    return (
        latestArtifacts.find((el) => artifactIdentity(el) === identity)?.id ??
        null
    );
};
